import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import IntervalInput, { parseIntervalInput } from "../../../components/inputs/IntervalInput";
import { sameVietaSet, sameVietaValue } from "../temat6_zadania_uzupelniajace_maturalne/podtemat4_rozszerzenie_types/vietaAnswerSets";

const TYPE = "QuadraticParameterVietaPractice";
const rand = (lo, hi) => lo + Math.floor(Math.random() * (hi - lo + 1));
const pick = (items) => items[rand(0, items.length - 1)];
const signed = (n) => n < 0 ? String(n) : `+${n}`;
const term = (n, symbol) => n === 0 ? "" : `${n < 0 ? "-" : "+"}${Math.abs(n) === 1 ? "" : Math.abs(n)}${symbol}`;
const linear = (a, b) => `${a === 1 ? "" : a === -1 ? "-" : a}m${b ? signed(b) : ""}`;
const quadratic = (a, b, c) => `${a === 1 ? "" : a === -1 ? "-" : a}m^2${term(b, "m")}${c ? signed(c) : ""}`;
const groupedLinear = (a, b) => a < 0 ? `-(${linear(-a, -b)})` : `+(${linear(a, b)})`;
const groupedQuadratic = (a, b, c) => a < 0 ? `-(${quadratic(-a, -b, -c)})` : `+(${quadratic(a, b, c)})`;
const factor = (root) => `(m${root ? signed(-root) : ""})`;
const open = (left, right) => ({ left, right, leftClosed: false, rightClosed: false });
const intervalLatex = (left, right) => `(${left === -Infinity ? "-\\infty" : left},${right === Infinity ? "+\\infty" : right})`;

function signTask(kind) {
  const u = rand(-4, 2);
  const v = u + rand(1, 4);
  const a = pick([-2, -1, 1, 2]);
  const sum = u + v;
  const product = u * v;
  const equation = `${a === 1 ? "" : a === -1 ? "-" : a}x^2${groupedLinear(-2 * a, a * sum)}x${groupedQuadratic(a, -a * sum, a * product)}=0`;
  const sets = {
    opposite: [open(u, v)],
    same: [open(-Infinity, u), open(v, Infinity)],
    positive: [open(v, Infinity)],
    negative: [open(-Infinity, u)],
  };
  const answers = {
    opposite: intervalLatex(u, v),
    same: `${intervalLatex(-Infinity, u)}\\cup${intervalLatex(v, Infinity)}`,
    positive: intervalLatex(v, Infinity),
    negative: intervalLatex(-Infinity, u),
  };
  const labels = {
    opposite: "przeciwnych znaków",
    same: "jednakowych znaków",
    positive: "obie dodatnie",
    negative: "obie ujemne",
  };
  return { type: TYPE, kind, a, u, v, equation, expected: sets[kind], answer: answers[kind], label: labels[kind] };
}

function leadingCoefficientTask() {
  const s = rand(-3, 2);
  const t = s + rand(1, 4);
  const h = rand(-3, 3);
  return { type: TYPE, kind: "leading", s, t, h,
    equation: `(${linear(1, -s)})x^2+(${linear(1, h)})x+(${linear(1, -t)})=0`,
    expected: [open(s, t)], answer: intervalLatex(s, t) };
}

function transformTask(kind) {
  const h = rand(-4, 4);
  const q = rand(1, 6);
  const equation = `x^2-(${linear(1, h)})x-${q}=0`;
  if (kind === "reciprocal") {
    const target = pick([-3, -2, -1, 1, 2, 3]);
    const answerValue = -q * target - h;
    return { type: TYPE, kind, h, q, equation, target, answerValue,
      condition: `\\dfrac1{x_1}+\\dfrac1{x_2}=${target}` };
  }
  const t = rand(1, 5);
  const target = t * t + 2 * q;
  return { type: TYPE, kind, h, q, t, target, equation,
    answerValues: [-h - t, -h + t], condition: `x_1^2+x_2^2=${target}` };
}

function thresholdTask(kind) {
  const u = rand(-3, 2);
  const v = u + rand(1, 4);
  const t = rand(-3, 3);
  const a = pick([-2, -1, 1, 2]);
  const sum = u + v;
  const equation = `${a === 1 ? "" : a === -1 ? "-" : a}x^2${groupedLinear(-2 * a, a * sum)}x${groupedQuadratic(a, -a * sum, a * u * v)}=0`;
  const bound = t + (kind === "right" ? v : u);
  return { type: TYPE, kind, u, v, t, a, equation, bound,
    expected: kind === "right" ? [open(bound, Infinity)] : [open(-Infinity, bound)],
    answer: kind === "right" ? intervalLatex(bound, Infinity) : intervalLatex(-Infinity, bound) };
}

export function generateTask(kind) {
  const selected = kind || pick(["opposite", "leading", "same", "positive", "negative", "reciprocal", "squares", "right", "left"]);
  if (selected === "leading") return leadingCoefficientTask();
  if (["opposite", "same", "positive", "negative"].includes(selected)) return signTask(selected);
  if (["reciprocal", "squares"].includes(selected)) return transformTask(selected);
  return thresholdTask(selected);
}

export function generateTasks() {
  return ["opposite", "leading", "same", "positive", "negative", "reciprocal", "squares", "right", "left"].map(generateTask);
}

export function renderTask(task) {
  if (task.type !== TYPE) return null;
  const intro = task.kind === "leading"
    ? "Wyznacz wartości parametru m, dla których równanie jest kwadratowe i ma dwa pierwiastki rzeczywiste przeciwnych znaków."
    : task.kind === "right" || task.kind === "left"
    ? `Wyznacz wartości parametru m, dla których równanie ma dwa różne pierwiastki rzeczywiste i oba są ${task.kind === "right" ? "większe" : "mniejsze"} od ${task.t}.`
    : task.kind === "reciprocal" || task.kind === "squares"
      ? "Wyznacz wartości parametru m, dla których równanie ma dwa różne pierwiastki rzeczywiste spełniające warunek:"
      : `Wyznacz wartości parametru m, dla których równanie ma dwa różne pierwiastki rzeczywiste ${task.label}.`;
  return <div><p>{intro}</p><BlockMath math={task.equation} />{task.condition && <BlockMath math={task.condition} />}</div>;
}

export function renderInput(task, value, onChange, disabled) {
  if (task.type !== TYPE) return null;
  return <IntervalInput value={value || ""} onChange={onChange} disabled={disabled}
    asString showPreview previewVariable="m" showRootFractionTools
    label="Zbiór wartości parametru m"
    placeholder={task.kind === "reciprocal" ? "np. {-2} lub m=-2" : task.kind === "squares" ? "np. {-2; 3}" : "np. (-∞; 2) ∪ (5; +∞)"} />;
}

export function checkAnswer(task, value) {
  if (task.type !== TYPE) return false;
  if (task.kind === "reciprocal") {
    if (sameVietaValue(value, task.answerValue)) return true;
    const parsed = parseIntervalInput(String(value || "").replace(/^m\s*(?:∈|in|=)\s*/i, ""));
    return parsed?.kind === "finite" && parsed.points.length === 1 && Math.abs(parsed.points[0] - task.answerValue) < 1e-8;
  }
  if (task.kind === "squares") {
    const parsed = parseIntervalInput(String(value || "").replace(/^m\s*(?:∈|in|=)\s*/i, ""));
    return parsed?.kind === "finite" && parsed.points.length === 2 &&
      [...parsed.points].sort((a, b) => a - b).every((n, i) => Math.abs(n - task.answerValues[i]) < 1e-8);
  }
  return sameVietaSet(value, task.expected);
}

export function renderAnswer(task) {
  if (task.type !== TYPE) return null;
  const answer = task.kind === "reciprocal" ? `m=${task.answerValue}`
    : task.kind === "squares" ? `m\\in\\{${task.answerValues.join(",")}\\}`
      : `m\\in ${task.answer}`;
  return <BlockMath math={answer} />;
}

export function renderUserAnswer(_task, value) { return String(value ?? ""); }

export function renderExplanation(task) {
  if (task.type !== TYPE) return null;
  if (task.kind === "leading") return <div>
    <p>Współczynnik przy <InlineMath math={String.raw`x^2`} /> to <InlineMath math={linear(1, -task.s)} />. Gdy <InlineMath math={`m=${task.s}`} />, równanie przestaje być kwadratowe, więc tę wartość wykluczamy.</p>
    <p>Dla pozostałych wartości z wzorów Viète’a otrzymujemy:</p>
    <BlockMath math={`x_1\\cdot x_2=\\frac{${linear(1, -task.t)}}{${linear(1, -task.s)}}<0`} />
    <p>Iloraz jest ujemny między jego miejscem zerowym i punktem, w którym mianownik jest zerem. Na tym przedziale iloczyn jest ujemny, co zarazem gwarantuje dwa różne rzeczywiste pierwiastki.</p>
    {renderAnswer(task)}
  </div>;
  if (["opposite", "same", "positive", "negative"].includes(task.kind)) {
    const product = `m^2${term(-(task.u + task.v), "m")}${task.u * task.v ? signed(task.u * task.v) : ""}`;
    const sum = linear(2, -(task.u + task.v));
    return <div>
      <p>Najpierw sprawdzamy, że współczynnik przy <InlineMath math={String.raw`x^2`} /> jest różny od zera. Tutaj <InlineMath math={`a=${task.a}\\neq0`} />.</p>
      <p>Po podzieleniu równania przez <InlineMath math={String(task.a)} /> wzory Viète’a dają:</p>
      <BlockMath math={`x_1+x_2=${sum},\\qquad x_1\\cdot x_2=${product}=${factor(task.u)}${factor(task.v)}`} />
      <p>Wyróżnik wynosi <InlineMath math={`\\Delta=${task.a * task.a * (task.v - task.u) ** 2}>0`} /> dla każdego <InlineMath math={String.raw`m`} />, więc pierwiastki zawsze są rzeczywiste i różne.</p>
      {task.kind === "opposite" && <p>Przeciwne znaki oznaczają <InlineMath math={String.raw`x_1\cdot x_2<0`} />. Iloczyn <InlineMath math={`${factor(task.u)}${factor(task.v)}`} /> jest ujemny między jego miejscami zerowymi.</p>}
      {task.kind === "same" && <p>Jednakowe, niezerowe znaki oznaczają <InlineMath math={String.raw`x_1\cdot x_2>0`} />. Iloczyn jest dodatni poza przedziałem między jego miejscami zerowymi. Końców nie włączamy, bo wtedy jeden pierwiastek jest zerem.</p>}
      {task.kind === "positive" && <p>Oba pierwiastki są dodatnie dokładnie wtedy, gdy <InlineMath math={String.raw`x_1\cdot x_2>0`} /> oraz <InlineMath math={String.raw`x_1+x_2>0`} />. Łączymy warunki dla iloczynu i sumy.</p>}
      {task.kind === "negative" && <p>Oba pierwiastki są ujemne dokładnie wtedy, gdy <InlineMath math={String.raw`x_1\cdot x_2>0`} /> oraz <InlineMath math={String.raw`x_1+x_2<0`} />. Łączymy warunki dla iloczynu i sumy.</p>}
      {(task.kind === "positive" || task.kind === "negative") && <BlockMath math={`P>0\\iff m<${task.u}\\;\\lor\\;m>${task.v},\\qquad S${task.kind === "positive" ? ">" : "<"}0\\iff m${task.kind === "positive" ? ">" : "<"}\\frac{${task.u + task.v}}2`} />}
      <p>Po przecięciu warunków dostajemy:</p>{renderAnswer(task)}
    </div>;
  }
  if (task.kind === "reciprocal" || task.kind === "squares") {
    return <div>
      <p>Tu <InlineMath math={`x_1+x_2=${linear(1, task.h)},\\quad x_1\\cdot x_2=-${task.q}`} />. Wyróżnik <InlineMath math={`\\Delta=(${linear(1, task.h)})^2+${4 * task.q}>0`} /> dla każdego parametru; iloczyn pierwiastków jest niezerowy.</p>
      {task.kind === "reciprocal" ? <>
        <p>Sprowadzamy odwrotności do wspólnego mianownika, a dopiero potem podstawiamy wzory Viète’a:</p>
        <BlockMath math={`\\frac1{x_1}+\\frac1{x_2}=\\frac{x_1+x_2}{x_1\\cdot x_2}=-\\frac{${linear(1, task.h)}}{${task.q}}=${task.target}`} />
      </> : <>
        <p>Korzystamy z tożsamości <InlineMath math={String.raw`x_1^2+x_2^2=(x_1+x_2)^2-2x_1\cdot x_2`} />:</p>
        <BlockMath math={`(${linear(1, task.h)})^2+${2 * task.q}=${task.target}\\iff (${linear(1, task.h)})^2=${task.t * task.t}`} />
        <p>Pierwiastkujemy z uwzględnieniem obu znaków: <InlineMath math={`${linear(1, task.h)}=\\pm${task.t}`} />.</p>
      </>}
      <p>Po rozwiązaniu równania względem parametru:</p>{renderAnswer(task)}
    </div>;
  }
  const p = `m${task.u + task.v < 0 ? "+" : "-"}\\frac{${Math.abs(task.u + task.v)}}2`;
  const ft = `${task.a}(${linear(-1, task.t + task.u)})(${linear(-1, task.t + task.v)})`;
  return <div>
    <p>Oznaczmy lewą stronę przez <InlineMath math={String.raw`f(x)`} />. Wyróżnik to <InlineMath math={`\\Delta=${task.a * task.a * (task.v - task.u) ** 2}>0`} />, więc są dwa różne pierwiastki. Ich środek, czyli odcięta wierzchołka, wynosi:</p>
    <BlockMath math={`p=-\\frac b{2a}=\\frac{x_1+x_2}2=${p}`} />
    <p>Żeby oba pierwiastki leżały {task.kind === "right" ? "na prawo" : "na lewo"} od <InlineMath math={String(task.t)} />, potrzebujemy odpowiednio <InlineMath math={task.kind === "right" ? `p>${task.t}` : `p<${task.t}`} /> oraz punktu <InlineMath math={String(task.t)} /> poza przedziałem między pierwiastkami. Ten drugi warunek zapisujemy jako <InlineMath math={`a\\,f(${task.t})>0`} />:</p>
    <BlockMath math={`f(${task.t})=${ft},\\qquad a\\,f(${task.t})>0\\iff${factor(task.t + task.u)}${factor(task.t + task.v)}>0`} />
    <BlockMath math={`p${task.kind === "right" ? ">" : "<"}${task.t}\\iff m${task.kind === "right" ? ">" : "<"}\\frac{${2 * task.t + task.u + task.v}}2`} />
    <p>{task.a < 0 ? "Ponieważ a jest ujemne, samo f(t)>0 dawałoby zły znak — trzeba uwzględnić kierunek ramion paraboli." : "Dla dodatniego a jest to równoważne warunkowi f(t)>0."} Równoważnie można użyć <InlineMath math={task.kind === "right" ? `x_1+x_2>2\\cdot${task.t}` : `x_1+x_2<2\\cdot${task.t}`} /> zamiast warunku na <InlineMath math={String.raw`p`} />.</p>
    <p>Przecięcie tych warunków daje:</p>{renderAnswer(task)}
  </div>;
}

export default { generateTask, generateTasks, renderTask, renderInput, checkAnswer, renderAnswer, renderUserAnswer, renderExplanation };
